/**
 * AudioFrame: the compact feature snapshot the AudioWorklet emits once per analysis hop.
 *
 * It travels between threads as a flat Float32Array (no objects, no per-hop allocation in the
 * worklet). The time is stored as two float32 values (hi + lo) because a single float32 loses
 * millisecond precision after a few hours, and long club sets are a requirement.
 */

export const NUM_BANDS = 8;
/** Band centre frequencies in Hz: roughly one octave apart, kick to air. */
export const BAND_CENTERS_HZ: readonly number[] = [60, 120, 250, 500, 1000, 2000, 4000, 8000];
export const SPECTRUM_BINS = 128;
export const WAVEFORM_SAMPLES = 512;
/** 4096 @ 48 kHz ≈ 11.7 Hz per bin: enough bass resolution for log-spaced bars. The spectrum
 * is not latency-critical; the band filterbank drives the fast, punchy features. */
export const FFT_SIZE = 4096;
/** Samples between frames. 256 @ 48 kHz ≈ 5.3 ms, ≈ 188 frames/s. */
export const HOP_SIZE = 256;

export const F = {
  T_HI: 0,
  T_LO: 1,
  RMS: 2,
  PEAK: 3,
  ONSET: 4,
  HIT: 5,
  BASS: 6,
  MID: 7,
  TREBLE: 8,
  CENTROID: 9,
  BANDS: 10,
  BAND_ONSETS: 10 + NUM_BANDS,
  SPECTRUM: 10 + 2 * NUM_BANDS,
  /** Melody pitch as a fractional MIDI note (0 = none yet), and how sure (0..1). */
  PITCH: 10 + 2 * NUM_BANDS + SPECTRUM_BINS,
  PITCH_CONF: 11 + 2 * NUM_BANDS + SPECTRUM_BINS,
  /** 12 pitch classes, C … B, 0..1. */
  CHROMA: 12 + 2 * NUM_BANDS + SPECTRUM_BINS,
  WAVEFORM: 24 + 2 * NUM_BANDS + SPECTRUM_BINS,
} as const;

export const FRAME_LENGTH = F.WAVEFORM + WAVEFORM_SAMPLES;

export function createFrame(): Float32Array {
  return new Float32Array(FRAME_LENGTH);
}

export function writeFrameTime(frame: Float32Array, t: number): void {
  const hi = Math.fround(t);
  frame[F.T_HI] = hi;
  frame[F.T_LO] = t - hi;
}

export function readFrameTime(frame: Float32Array): number {
  return (frame[F.T_HI] ?? 0) + (frame[F.T_LO] ?? 0);
}
